import { useState, useEffect } from 'react'
import { Wallet, Check, ChevronRight, Edit3 } from 'lucide-react'
import { Modal } from '@/components/Modal'
import { ChainIcon } from '@/components/ChainIcon'
import { useThemeColors } from '@/hooks/useThemeColors'
import { CHAIN_REGISTRY } from '@/config/chains'
import { WalletLogos, WALLET_LOGO_KEY } from '@/components/icons/WalletLogos'
import { FONT_BODY, FONT_MONO } from '@/config/theme'
import { isWalletTypeLabelEnabled } from '@/config/flags'
import { isRenderableWalletLabel } from '@/utils/walletLabel'
import type { Chain } from '@/types'

interface RecipientModalProps {
  show: boolean
  onClose: () => void
  destChain: Chain | null
  /** The SOURCE chain — used to detect when source and destination share a wallet ecosystem. Connecting a
   *  wallet swaps the one active account for that ecosystem, so offering "connect a different wallet" for a
   *  same-ecosystem destination would hijack the source; in that case we steer to paste instead. */
  sourceChain?: Chain | null
  /** The token that ARRIVES at the recipient (dest/receive token symbol, e.g. USDC / USDT / ETH). Drives
   *  the "Where should your X arrive?" copy so it reflects the actual asset, not a hardcoded "USDC". */
  tokenSymbol?: string
  connectedAddress: string | null
  /**
   * #261 F3 (parent spec item 3) — the canonical `walletKind` of the wallet connected on the
   * DESTINATION chain, from the shared normaliser (`utils/walletLabel.ts`). Brands the "Use connected
   * address" option's LOGO so the user sees their MetaMask/Phantom icon, not their Robinhood app's.
   *
   * FIXER PASS, F-3 — LOGO ONLY. This prop used to ALSO drive the option's text (a bare
   * `{connectedWalletKind}`), which bypassed every rule `buildRecipientWalletLabel` enforces for the
   * review card: the WalletConnect "via" transport wording (H6), the destination-network half (A1 —
   * the load-bearing half), and the brand-collision " Wallet" suffix (H5). See `connectedWalletLabel`
   * below for the text; this prop is now icon-lookup only (`WALLET_LOGO_KEY[connectedWalletKind]`).
   *
   * `null`/absent (which includes every flag-OFF render — the parent gates it) → the generic wallet
   * glyph.
   */
  connectedWalletKind?: string | null
  /**
   * #261 F3 fixer pass, F-3 — the option's NAME text: the SAME `MetaMask · Robinhood network` /
   * `via WalletConnect · Base network` / `your connected wallet · Solana network` shape the review
   * card renders, built by the parent through the identical `buildRecipientWalletLabel` call
   * (`utils/walletLabel.ts`) so all three of its rules apply here too — this is where the recipient is
   * CHOSEN, one screen before the review card confirms it, so the two must tell the same story about
   * one address.
   *
   * Re-gated HERE independently of the parent, mirroring `ReviewTransferModal`'s
   * `resolvedRecipientLabel`: a prop cannot vouch for its own producer. `isWalletTypeLabelEnabled()`
   * reproduces H2 (flag OFF → this option renders no name, byte-identical to today) and
   * `isRenderableWalletLabel` reproduces H3 (an address-shaped value is refused rather than rendered,
   * so a mis-wire can never put a second recipient string on this row either).
   */
  connectedWalletLabel?: string | null
  onUseConnected: (address: string) => void
  onPasteAddress: (address: string) => void
  onConnectWallet: (family: 'evm' | 'solana' | 'tron') => void
}

function validateAddress(addr: string, addrType: 'evm' | 'solana' | 'tron'): { ok: boolean; reason?: string } {
  const trimmed = addr.trim()
  if (!trimmed) return { ok: false, reason: 'Address is required' }

  if (addrType === 'evm') {
    if (!/^0x[0-9a-fA-F]{40}$/.test(trimmed)) {
      return { ok: false, reason: 'Not a valid EVM address (expected 0x + 40 hex characters)' }
    }
    return { ok: true }
  }

  if (addrType === 'tron') {
    // Reject hex-41 format with helpful message
    if (/^41[0-9a-fA-F]{40}$/.test(trimmed)) {
      return { ok: false, reason: 'Please use T-prefix format (not hex). Paste the base58 address starting with T.' }
    }
    return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(trimmed)
      ? { ok: true }
      : { ok: false, reason: 'Invalid Tron address (must start with T, 34 characters)' }
  }

  // Solana: base58, 32-44 characters
  if (!/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(trimmed)) {
    return { ok: false, reason: 'Not a valid Solana address (expected base58, 32-44 characters)' }
  }
  return { ok: true }
}

function shortAddr(addr: string): string {
  if (addr.length > 16) return `${addr.slice(0, 8)}...${addr.slice(-8)}`
  return addr
}

export function RecipientModal({
  show,
  onClose,
  destChain,
  sourceChain,
  tokenSymbol,
  connectedAddress,
  connectedWalletKind,
  connectedWalletLabel,
  onUseConnected,
  onPasteAddress,
  onConnectWallet,
}: RecipientModalProps) {
  const c = useThemeColors()
  const [manualInput, setManualInput] = useState('')
  const [inputError, setInputError] = useState<string | null>(null)

  // Reset state when modal opens (prevents stale paste input on reopen)
  useEffect(() => {
    if (show) {
      setManualInput('')
      setInputError(null)
    }
  }, [show])

  const chainName = destChain ? CHAIN_REGISTRY[destChain].name : 'destination'
  const addrType = destChain ? CHAIN_REGISTRY[destChain].addrType : 'evm'
  const walletFamily = addrType === 'tron' ? 'Tron' : addrType === 'solana' ? 'Solana' : 'EVM'
  // Chain-aware paste placeholder — hints the address format for the destination family.
  const addrPlaceholder = addrType === 'evm' ? '0x…' : addrType === 'tron' ? 'T…' : 'Solana address…'

  // Connecting a wallet swaps the ONE active account for that ecosystem (wagmi/EVM, Solana adapter, Tron).
  // When the SOURCE is the same ecosystem as the destination, connecting a "different" recipient wallet
  // would hijack the source too — so we suppress that option and steer to paste (which never touches
  // connections). Cross-ecosystem (e.g. EVM source → Solana dest) uses a separate slot and is safe.
  const sourceAddrType = sourceChain ? CHAIN_REGISTRY[sourceChain].addrType : null
  const sameEcosystemAsSource = sourceAddrType !== null && sourceAddrType === addrType

  // #261 F3 — the connected wallet's brand, via the existing WALLET_LOGO_KEY → WalletLogos seam.
  // Null kind (unknown connector, or the flag off at the parent) → the generic glyph and no name,
  // which is byte-identical to this option's pre-F3 render.
  const connectedLogoKey = connectedWalletKind ? WALLET_LOGO_KEY[connectedWalletKind] : null
  const ConnectedLogo = connectedLogoKey ? WalletLogos[connectedLogoKey] : null

  // Fixer pass, F-3 — re-gate the NAME text independently of the parent (mirrors
  // ReviewTransferModal's `resolvedRecipientLabel`): the flag AND the fail-closed address guard are
  // re-checked here rather than trusted from the prop, so this row can never grow a second, looser
  // recipient string (H3) and can never show a name while the flag is off (H2), regardless of what
  // the parent passes.
  const walletLabelOn = isWalletTypeLabelEnabled()
  const resolvedConnectedWalletLabel =
    walletLabelOn && isRenderableWalletLabel(connectedWalletLabel) ? connectedWalletLabel : null

  const handleClose = () => {
    setManualInput('')
    setInputError(null)
    onClose()
  }

  const handleSubmitManual = () => {
    const v = validateAddress(manualInput, addrType)
    if (!v.ok) {
      setInputError(v.reason ?? 'Invalid address')
      return
    }
    onPasteAddress(manualInput.trim())
    setManualInput('')
    setInputError(null)
  }

  // Live validity for the paste field — drives the inline error + the confirm button's enabled state so an
  // invalid address is flagged as the user types/pastes, not only after clicking "Use this address".
  const manualValid = manualInput.trim().length > 0 && validateAddress(manualInput, addrType).ok

  const cardStyle = {
    width: '100%', padding: 16, borderRadius: 14,
    background: c.blB, border: `1px solid ${c.dv}`,
    display: 'flex' as const, justifyContent: 'space-between' as const,
    alignItems: 'center' as const, cursor: 'pointer' as const,
    transition: 'border-color 0.15s',
    fontFamily: FONT_BODY,
  }

  return (
    <Modal show={show} onClose={handleClose} title="Send to a different address">
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Description — Shane copy: chain- and token-aware, e.g. "Paste the Base address that will receive your USDC". */}
        <div style={{ fontSize: 13, color: c.mu, lineHeight: 1.6, fontFamily: FONT_BODY }}>
          Paste the {chainName} address that will receive your {tokenSymbol || 'funds'}.
        </div>

        {/* Option 1: Use the connected dest-chain wallet (shown when a wallet-derived address exists) */}
        {connectedAddress && (
          <button
            onClick={() => onUseConnected(connectedAddress)}
            style={{ ...cardStyle, border: `1px solid ${c.dv}` }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'rgba(34, 197, 94, 0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {ConnectedLogo ? <ConnectedLogo size={20} /> : <Wallet size={16} color={c.ac} />}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: c.tx }}>
                  Use connected address
                </div>
                <div style={{
                  fontSize: 12, color: c.mu, marginTop: 2,
                  fontFamily: FONT_MONO, fontVariantNumeric: 'tabular-nums',
                }}>
                  {shortAddr(connectedAddress)}
                </div>
                {/* #261 F3 / fixer F-3 — the provider NAME, routed through the SAME
                    `buildRecipientWalletLabel` shape the review card renders: wallet + NETWORK
                    (A1), `via WalletConnect` for a WC session (H6), and the brand-collision
                    " Wallet" suffix (H5) — never the bare kind this used to print. */}
                {resolvedConnectedWalletLabel && (
                  <div
                    data-testid="recipient-connected-wallet-name"
                    style={{ fontSize: 12, color: c.mu, marginTop: 2, fontFamily: FONT_BODY }}
                  >
                    {resolvedConnectedWalletLabel}
                  </div>
                )}
              </div>
            </div>
            <Check size={18} color={c.ac} />
          </button>
        )}

        {/* Option 1b: Connect a wallet for the dest chain family. Shown for the initial connect, and — once
            a wallet is connected — ONLY when the destination is a DIFFERENT ecosystem than the source, since
            connecting a same-ecosystem wallet would swap the single active account and hijack the source
            (#107). For a same-ecosystem different recipient, the paste field below is the safe path. */}
        {destChain && (!connectedAddress || !sameEcosystemAsSource) && (
          <button
            onClick={() => onConnectWallet(addrType === 'tron' ? 'tron' : addrType === 'solana' ? 'solana' : 'evm')}
            style={cardStyle}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 28, height: 28,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <ChainIcon chain={destChain} size={28} />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: c.tx }}>
                  {connectedAddress ? `Connect a different ${walletFamily} wallet` : `Connect ${walletFamily} wallet`}
                </div>
                <div style={{ fontSize: 12, color: c.mu, marginTop: 2 }}>
                  Use {connectedAddress ? 'another' : 'a'} {walletFamily} wallet as the recipient
                </div>
              </div>
            </div>
            <ChevronRight size={16} color={c.mu} />
          </button>
        )}

        {/* Same-ecosystem source & dest with a wallet already connected: connecting another wallet would
            hijack the source, so we hid that option above — point the user to paste as the safe way to reach
            a different same-ecosystem address. */}
        {connectedAddress && sameEcosystemAsSource && (
          <div style={{ fontSize: 12, color: c.mu, lineHeight: 1.5, fontFamily: FONT_BODY, marginTop: -4 }}>
            To send to a different {walletFamily} address, paste it below — connecting another {walletFamily}{' '}
            wallet would also change your source wallet.
          </div>
        )}

        {/* Option 2: Paste an address (always shown) */}
        <div style={{
          padding: 16, borderRadius: 14,
          background: c.blB, border: `1px solid ${c.dv}`,
          fontFamily: FONT_BODY,
        }}>
          <input
            type="text"
            value={manualInput}
            onChange={(e) => {
              const val = e.target.value
              setManualInput(val)
              // Validate live: clear the error while empty, otherwise surface the format reason immediately.
              const t = val.trim()
              if (!t) { setInputError(null); return }
              const v = validateAddress(val, addrType)
              setInputError(v.ok ? null : (v.reason ?? 'Invalid address'))
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && manualValid) {
                e.preventDefault()
                handleSubmitManual()
              }
            }}
            placeholder={addrPlaceholder}
            maxLength={100}
            aria-label={`Recipient ${chainName} wallet address`}
            aria-invalid={!!inputError}
            aria-describedby={inputError ? 'manual-address-error' : undefined}
            style={{
              width: '100%', padding: 12, borderRadius: 10,
              fontSize: 13, fontFamily: FONT_MONO,
              background: c.iB,
              border: `1px solid ${inputError ? c.eC : c.dv}`,
              color: c.tx, outline: 'none',
              transition: 'border-color 0.15s',
              boxSizing: 'border-box',
            }}
          />
          {inputError && (
            <div
              id="manual-address-error"
              role="alert"
              style={{ fontSize: 12, marginTop: 6, color: c.eC }}
            >
              {inputError}
            </div>
          )}

          {/* "Use this address" confirm button */}
          <button
            onClick={handleSubmitManual}
            disabled={!manualValid}
            style={{
              width: '100%', marginTop: 12, padding: 16, borderRadius: 14,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: manualValid ? 'rgba(34, 197, 94, 0.07)' : c.iB,
              border: `1px solid ${manualValid ? c.gr : c.dv}`,
              cursor: manualValid ? 'pointer' : 'default',
              transition: 'all 0.15s', fontFamily: FONT_BODY,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: c.gr, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Edit3 size={14} />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: c.tx }}>
                  Use this address
                </div>
              </div>
            </div>
            {manualValid ? (
              <Check size={18} color={c.gr} />
            ) : (
              <ChevronRight size={16} color={c.mu} />
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}
